import { Form, Input, DatePicker, Select, Button, Space, message } from 'antd';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

interface EmailTemplate {
  id: string;
  name: string;
  isActive: boolean;
}

interface EventFormProps {
  event?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function EventForm({ event, onSuccess, onCancel }: EventFormProps) {
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [forms, setForms] = useState<any[]>([]);

  useEffect(() => {
    fetchTemplates();
    fetchForms();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch('/api/v1/feedback/email-templates', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setTemplates(data.filter((t: EmailTemplate) => t.isActive));
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchForms = async () => {
    try {
      const res = await fetch('/api/v1/form-builder/forms', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setForms(data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        name: values.name,
        description: values.description,
        slug: values.slug,
        startDate: values.dateRange[0].toISOString(),
        endDate: values.dateRange[1].toISOString(),
        location: values.location,
        eventType: values.eventType,
        formId: values.formId || null,
        emailTemplateId: values.emailTemplateId || null,
        isActive: values.isActive !== undefined ? values.isActive : true,
      };

      const url = event
        ? `/api/v1/feedback/events/${event.id}`
        : '/api/v1/feedback/events';
      
      const method = event ? 'PUT' : 'POST';

      await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(data),
      });

      message.success(event ? 'Event updated successfully' : 'Event created successfully');
      onSuccess();
    } catch (error) {
      message.error('Failed to save event');
    }
  };

  const initialValues = event
    ? {
        name: event.name,
        description: event.description,
        slug: event.slug,
        dateRange: [dayjs(event.startDate), dayjs(event.endDate)],
        location: event.location,
        eventType: event.eventType,
        formId: event.formId,
        emailTemplateId: event.emailTemplateId,
        isActive: event.isActive,
      }
    : {
        isActive: true,
      };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Form.Item
        label="Event Name"
        name="name"
        rules={[{ required: true, message: 'Please enter event name' }]}
      >
        <Input placeholder="MaximoWorld 2025" />
      </Form.Item>

      <Form.Item
        label="Slug"
        name="slug"
        rules={[{ required: true, message: 'Please enter slug' }]}
        help="URL-friendly identifier (e.g., maximoworld-2025)"
      >
        <Input placeholder="maximoworld-2025" />
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
      >
        <Input.TextArea rows={3} placeholder="Event description..." />
      </Form.Item>

      <Form.Item
        label="Date Range"
        name="dateRange"
        rules={[{ required: true, message: 'Please select date range' }]}
      >
        <DatePicker.RangePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        label="Location"
        name="location"
        rules={[{ required: true, message: 'Please enter location' }]}
      >
        <Input placeholder="Las Vegas Booth #213" />
      </Form.Item>

      <Form.Item
        label="Event Type"
        name="eventType"
        rules={[{ required: true, message: 'Please select event type' }]}
      >
        <Select placeholder="Select type">
          <Select.Option value="conference">Conference</Select.Option>
          <Select.Option value="webinar">Webinar</Select.Option>
          <Select.Option value="workshop">Workshop</Select.Option>
          <Select.Option value="meetup">Meetup</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Feedback Form"
        name="formId"
        help="Select a form for collecting feedback (optional)"
      >
        <Select placeholder="Select feedback form" allowClear>
          {forms.filter(f => !f.isTemplate).map((formItem) => (
            <Select.Option key={formItem.id} value={formItem.id}>
              {formItem.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Email Template"
        name="emailTemplateId"
        help="Select the email template to send after feedback submission"
      >
        <Select placeholder="Select email template" allowClear>
          {templates.map((template) => (
            <Select.Option key={template.id} value={template.id}>
              {template.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Status"
        name="isActive"
        rules={[{ required: true }]}
      >
        <Select>
          <Select.Option value={true}>Active</Select.Option>
          <Select.Option value={false}>Inactive</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            {event ? 'Update' : 'Create'}
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
